
import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import FeedbacksList from '@/components/candidate/FeedbacksList';
import FeedbacksGivenList from '@/components/candidate/FeedbacksGivenList';
import FeedbackRequestsList from '@/components/feedback-requests/FeedbackRequestsList';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import type { UserRole } from '@/types/feedback';
import { Badge } from '@/components/ui/badge';
import { CreditCard } from 'lucide-react';
import { useBillingPlans } from '@/hooks/useBillingPlans';

const AdminTabs = () => {
  const [activeContext, setActiveContext] = useState<UserRole>('individual');
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { billingPlans } = useBillingPlans();

  useEffect(() => {
    fetchAvailableRoles();
  }, []);

  const fetchAvailableRoles = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .order('role')
        .not('role', 'is', null);
      
      if (error) throw error;
      
      const roles = [...new Set(data?.map(item => item.role))];
      setAvailableRoles(roles);
    } catch (error) {
      console.error('Error fetching available roles:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les rôles disponibles.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleContextChange = (value: string) => {
    // Convert to UserRole type before setting
    setActiveContext(safeUserRole(value));
    sessionStorage.setItem('adminContext', value);
  };

  const getPlanBadge = (planId: string) => {
    const plan = billingPlans.find(p => p.id === planId);
    if (!plan) return null;

    return (
      <Badge className={plan.is_enterprise ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"}>
        <CreditCard className="h-3 w-3 mr-1" /> {plan.name}
      </Badge>
    );
  };

  // Helper function to safely convert string to UserRole
  const safeUserRole = (role: string): UserRole => {
    // Check if role is already a valid UserRole
    if (role === 'individual' || role === 'corporate' || role === 'admin') {
      return role as UserRole;
    }
    
    // Convert legacy roles
    if (role === 'candidat') return 'individual';
    if (role === 'entreprise' || role === 'recruteur') return 'corporate';
    
    // Default fallback
    return 'individual';
  };

  const getPlanForRole = (role: string): string => {
    const normalizedRole = safeUserRole(role);
    if (normalizedRole === 'corporate') {
      return 'enterprise';
    }
    return 'free';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Visualisation par rôle</h2>
        <div className="flex items-center gap-4">
          {activeContext && (
            <div className="hidden md:flex items-center">
              <span className="text-sm text-muted-foreground mr-2">Forfait actif:</span>
              {getPlanBadge(getPlanForRole(activeContext))}
            </div>
          )}
          <Select defaultValue={activeContext} onValueChange={handleContextChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Sélectionner un rôle" />
            </SelectTrigger>
            <SelectContent>
              {loading ? (
                <SelectItem value="loading" disabled>Chargement des rôles...</SelectItem>
              ) : (
                availableRoles.map(role => (
                  <SelectItem key={role} value={role}>
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="feedbacksReceived" className="w-full">
        <TabsList className="grid grid-cols-4 mb-8">
          <TabsTrigger value="feedbacksReceived">Feedbacks reçus</TabsTrigger>
          <TabsTrigger value="feedbacksGiven">Feedbacks donnés</TabsTrigger>
          <TabsTrigger value="requests">Demandes reçues</TabsTrigger>
          <TabsTrigger value="sent">Demandes envoyées</TabsTrigger>
        </TabsList>
        
        <TabsContent value="feedbacksReceived">
          <FeedbacksList />
        </TabsContent>
        
        <TabsContent value="feedbacksGiven">
          <FeedbacksGivenList />
        </TabsContent>
        
        <TabsContent value="requests">
          <FeedbackRequestsList type="received" context={activeContext} />
        </TabsContent>
        
        <TabsContent value="sent">
          <FeedbackRequestsList type="sent" context={activeContext} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminTabs;
